import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback } from 'react';
const CSS = `
.cg-toast-container {
  position: fixed;
  top: var(--space-5);
  right: var(--space-5);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  pointer-events: none;
}
.cg-toast {
  display: flex; align-items: center; gap: var(--space-3);
  padding: 14px 16px; background: var(--surface-card);
  border-radius: var(--radius-xl); box-shadow: var(--shadow-xl);
  min-width: 300px; max-width: 420px; pointer-events: auto;
  border-left: 4px solid var(--slate-400); font-family: var(--font-sans);
}
@media (prefers-reduced-motion: no-preference) {
  .cg-toast { animation: cgToastIn var(--duration-slow) var(--ease-out); }
  @keyframes cgToastIn { from { transform: translateX(16px); opacity: 0; } to { transform: none; opacity: 1; } }
}
.cg-toast__icon { flex-shrink: 0; display: inline-flex; }
.cg-toast__icon svg { width: 20px; height: 20px; }
.cg-toast__body { flex: 1; min-width: 0; }
.cg-toast__title { font-size: var(--text-sm); font-weight: var(--weight-bold); color: var(--text-heading); }
.cg-toast__msg { font-size: var(--text-sm); font-weight: var(--weight-medium); color: var(--text-body); line-height: var(--leading-snug); }
.cg-toast__close {
  flex-shrink: 0; background: none; border: none; cursor: pointer; color: var(--text-subtle);
  width: 24px; height: 24px; border-radius: var(--radius-sm); display: inline-flex;
  align-items: center; justify-content: center;
  transition: background var(--duration-fast), color var(--duration-fast); padding: 0;
}
.cg-toast__close:hover { background: var(--slate-100); color: var(--text-body); }
.cg-toast__close svg { width: 16px; height: 16px; }
.cg-toast--success { border-left-color: var(--green-500); background: linear-gradient(135deg, var(--green-50) 0%, var(--surface-card) 60%); }
.cg-toast--error   { border-left-color: var(--red-500);   background: linear-gradient(135deg, var(--red-50)   0%, var(--surface-card) 60%); }
.cg-toast--warning { border-left-color: var(--amber-500); background: linear-gradient(135deg, var(--amber-50) 0%, var(--surface-card) 60%); }
.cg-toast--info    { border-left-color: var(--azure-500); background: linear-gradient(135deg, var(--azure-50) 0%, var(--surface-card) 60%); }
.cg-toast--success .cg-toast__icon { color: var(--green-600); }
.cg-toast--error   .cg-toast__icon { color: var(--red-600); }
.cg-toast--warning .cg-toast__icon { color: var(--amber-600); }
.cg-toast--info    .cg-toast__icon { color: var(--azure-600); }
`;
function injectStyles() {
    if (typeof document !== 'undefined' && !document.getElementById('cg-toast-css')) {
        const s = document.createElement('style');
        s.id = 'cg-toast-css';
        s.textContent = CSS;
        document.head.appendChild(s);
    }
}
const ToastContext = createContext(undefined);
export function ToastProvider({ children }) {
    injectStyles();
    const [toasts, setToasts] = useState([]);
    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    const showToast = useCallback((message, type = 'info', duration = 3000, title) => {
        const id = Math.random().toString(36).slice(2, 11);
        setToasts((prev) => [...prev, { id, message, type, duration, title }]);
        if (duration > 0)
            setTimeout(() => removeToast(id), duration);
    }, [removeToast]);
    return (_jsxs(ToastContext.Provider, { value: { toasts, showToast, removeToast }, children: [children, _jsx(ToastContainer, { toasts: toasts, onClose: removeToast })] }));
}
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error('useToast must be used within ToastProvider');
    return ctx;
}
const ICONS = {
    success: (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M20 6 9 17l-5-5" }) })),
    error: (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("path", { d: "m15 9-6 6M9 9l6 6" })] })),
    warning: (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" }), _jsx("path", { d: "M12 9v4M12 17h.01" })] })),
    info: (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("path", { d: "M12 16v-4M12 8h.01" })] })),
};
const CloseIcon = () => (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "m18 6-12 12M6 6l12 12" }) }));
function ToastContainer({ toasts, onClose }) {
    return (_jsx("div", { className: "cg-toast-container", children: toasts.map((t) => (_jsxs("div", { className: `cg-toast cg-toast--${t.type}`, role: "status", children: [_jsx("span", { className: "cg-toast__icon", children: ICONS[t.type] }), _jsxs("div", { className: "cg-toast__body", children: [t.title && _jsx("div", { className: "cg-toast__title", children: t.title }), _jsx("div", { className: "cg-toast__msg", children: t.message })] }), _jsx("button", { className: "cg-toast__close", "aria-label": "Dismiss", onClick: () => onClose(t.id), children: _jsx(CloseIcon, {}) })] }, t.id))) }));
}
